import { Component, Input, OnDestroy } from '@angular/core';
import { Association, AssociationManager, AssociationMember } from '../../../../../../../models/association';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AssociationService } from '../../../../../../../services/association/association.service';
import { Router } from '@angular/router';
import { batchAll } from '../../../../../../../utils';


@Component({
  selector: 'app-remove-association-modal',
  templateUrl: './remove-association-modal.component.html',
  styleUrls: ['./remove-association-modal.component.css']
})
export class RemoveAssociationModalComponent implements OnDestroy {

  @Input() association: Association;

  public confirmChecked = false;
  public modalClosed = false;

  public errorMessage = '';
  public serverError: number = null;
  public loadingRemoveMembers = false;
  public loadingRemoveManagers = false;
  public loadingRemoveAssociation = false;

  constructor(
    public activeModal: NgbActiveModal,
    public associationService: AssociationService,
    private router: Router
  ) {}

  ngOnDestroy() {
    this.modalClosed = true;
  }

  public handleSubmit() {
    if (!this.confirmChecked) { return; }

    this.removeMembers()
      .then(() => {
        return this.removeManagers();
      })
      .then(() => {
        return this.removeAssociation();
      })
      .then(() => {
        this.router.navigate(
          ['/associations'],
          {
            state: {
              feedback: {
                text: this.association.name + ' has been removed.',
                status: 'success'
              }
            }
          }
        );

        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to remove this association.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingRemoveMembers = false;
        this.loadingRemoveManagers = false;
        this.loadingRemoveAssociation = false;
      });
  }

  public isLoading() {
    return (
      this.loadingRemoveMembers ||
      this.loadingRemoveManagers ||
      this.loadingRemoveAssociation
    );
  }

  public getLoadingMessage() {
    if (this.loadingRemoveMembers) {
      return 'Removing members...';
    } else if (this.loadingRemoveManagers) {
      return 'Removing managers...';
    } else if (this.loadingRemoveAssociation) {
      return 'Removing association...';
    } else {
      return 'Loading...';
    }
  }

  private removeMembers(): Promise<void> {
    // Halt removal if the modal is closed.
    if (this.modalClosed) { return Promise.reject(); }

    return this.associationService
      .getAssociationMembers(
        1,
        100,
        {
          association_id: this.association.id
        }
      )
      .then(result => {
        if (result.results.length === 0) {
          return Promise.resolve();
        }
        return batchAll(
          5,
          result.results,
          (am: AssociationMember) => {
            // Halt removal if the modal is closed.
            if (this.modalClosed) { return Promise.reject(); }
            return this.associationService.removeAssociationMember(am.url);
          }
        )
          .then()
          .then(() => {
            return this.removeMembers();
          });
      });
  }

  private removeManagers(): Promise<void> {
    // Halt removal if the modal is closed.
    if (this.modalClosed) { return Promise.reject(); }

    return this.associationService
      .getAssociationManagers(
        1,
        100,
        {
          association_id: this.association.id
        }
      )
      .then(result => {
        if (result.results.length === 0) {
          return Promise.resolve();
        }
        return batchAll(
          5,
          result.results,
          (am: AssociationManager) => {
            // Halt removal if the modal is closed.
            if (this.modalClosed) { return Promise.reject(); }
            return this.associationService.removeAssociationManager(am.url);
          }
        )
          .then()
          .then(() => {
            return this.removeManagers();
          });
      });
  }

  private removeAssociation(): Promise<void> {
    return this.associationService.removeAssociation(this.association.url);
  }

}
